import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { videoAPI, presetAPI, generateAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Sparkles, Copy, Check, Loader2, ArrowLeft, Hash, Type, FileText, MessageSquare, Tag, ChevronDown,
  Youtube, Instagram, Twitter, Facebook, Linkedin, Music2, Globe
} from 'lucide-react';
import { toast } from 'sonner';

const PLATFORM_ICONS = {
  youtube: Youtube, instagram: Instagram, twitter: Twitter, x: Twitter,
  facebook: Facebook, linkedin: Linkedin, tiktok: Music2,
};

function getPlatformIcon(platform) {
  const key = platform.toLowerCase().split(/[\s/]+/)[0];
  return PLATFORM_ICONS[key] || Globe;
}

function cleanHashtags(hashtags) {
  return (hashtags || []).map(h => h.replace(/^#/, ''));
}

export default function GeneratePage() {
  const { videoId, presetId } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [preset, setPreset] = useState(null);
  const [generations, setGenerations] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const { refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('0');
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    loadData();
  }, [videoId, presetId]);

  const loadData = async () => {
    try {
      const [videoRes, presetRes, gensRes] = await Promise.all([
        videoAPI.get(videoId),
        presetAPI.get(presetId),
        generateAPI.list(videoId, presetId),
      ]);
      setVideo(videoRes.data);
      setPreset(presetRes.data);
      setGenerations(gensRes.data);
      if (gensRes.data.length > 0) setActiveTab('0');
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await generateAPI.create(videoId, presetId);
      const gensRes = await generateAPI.list(videoId, presetId);
      setGenerations(gensRes.data);
      setActiveTab('0');
      refreshUser();
      toast.success('New option generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async (text, field) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleCopyAll = (gen) => {
    const htags = cleanHashtags(gen.hashtags);
    const tags = gen.tags || htags;
    const all = [
      gen.title,
      '',
      gen.description,
      '',
      htags.map(t => `#${t}`).join(' '),
      '',
      `Tags: ${tags.join(', ')}`,
      gen.cta_suggestion ? `\nCTA: ${gen.cta_suggestion}` : '',
    ].join('\n');
    handleCopy(all, `all-${gen.id}`);
  };

  const CopyBtn = ({ text, field, testId }) => (
    <Button variant="ghost" size="sm" onClick={() => handleCopy(text, field)}
      className="text-muted-foreground hover:text-primary h-7 px-2" data-testid={testId}>
      {copiedField === field ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </Button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8 max-w-4xl mx-auto" data-testid="generate-page">
      {/* Back + Header */}
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate(`/video/${videoId}`)} className="text-muted-foreground hover:text-foreground mb-4" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Video
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">{video?.filename}</h1>
            <div className="flex items-center gap-2 mt-2 flex-nowrap overflow-x-auto" data-testid="generate-tags-row">
              <Link to="/presets" className="shrink-0 group" data-testid="preset-tag-link">
                <Badge variant="secondary" className="bg-primary/10 text-primary border-0 text-xs cursor-pointer transition-colors group-hover:bg-primary/20 group-hover:text-primary">
                  <Sparkles className="w-3 h-3 mr-1 shrink-0" />{preset?.name}
                </Badge>
              </Link>
              {preset?.targetPlatforms?.map((p, i) => {
                const Icon = getPlatformIcon(p);
                return (
                  <Badge key={i} variant="outline" className="text-[10px] text-muted-foreground shrink-0 gap-1" data-testid={`platform-tag-${i}`}>
                    <Icon className="w-3 h-3 shrink-0" />{p}
                  </Badge>
                );
              })}
              {preset?.tone && (
                <Badge variant="outline" className="text-[10px] text-muted-foreground shrink-0" data-testid="tone-tag">
                  {preset.tone}
                </Badge>
              )}
            </div>
          </div>
          <Button onClick={handleGenerate} disabled={generating}
            className="rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-indigo-500/25 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            data-testid="generate-button">
            {generating
              ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</>
              : <><Sparkles className="w-4 h-4 mr-2" /> {generations.length === 0 ? 'Generate Content' : 'Generate New Option'}</>
            }
          </Button>
        </div>
      </div>

      {/* Empty State */}
      {generations.length === 0 && !generating && (
        <Card className="bg-zinc-900/60 border-white/5" data-testid="no-generations">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-primary" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-2">Ready to Generate</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
              Click "Generate Content" to create optimized social post copy from your transcript using the {preset?.name} preset.
            </p>
            <Button onClick={handleGenerate} className="rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-indigo-500/25" data-testid="first-generate-button">
              <Sparkles className="w-4 h-4 mr-2" /> Generate Content
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Generating Overlay */}
      {generating && generations.length === 0 && (
        <Card className="bg-zinc-900/60 border-primary/20" data-testid="generating-state">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin mx-auto mb-6" />
            <h3 className="font-heading text-xl font-semibold">Creating Your Social Post</h3>
            <p className="text-sm text-muted-foreground mt-2">Analyzing transcript and crafting content...</p>
          </CardContent>
        </Card>
      )}

      {/* Generated Results */}
      {generations.length > 0 && (
        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="generations-tabs">
          <div className="flex items-center justify-between mb-4">
            <TabsList className="bg-secondary/40 overflow-x-auto max-w-[70%] flex-wrap h-auto gap-1 p-1">
              {generations.map((gen, i) => (
                <TabsTrigger key={gen.id} value={String(i)}
                  className="data-[state=active]:bg-primary/20 data-[state=active]:text-primary text-xs"
                  data-testid={`tab-option-${i}`}>
                  Option {generations.length - i}
                </TabsTrigger>
              ))}
            </TabsList>
            <p className="text-xs text-muted-foreground shrink-0 ml-2">
              {generations.length} {generations.length === 1 ? 'option' : 'options'} &middot; Latest first
            </p>
          </div>

          {generations.map((gen, i) => {
            const htags = cleanHashtags(gen.hashtags);
            const tags = gen.tags?.length > 0 ? gen.tags : htags;
            return (
              <TabsContent key={gen.id} value={String(i)} data-testid={`generation-content-${i}`}>
                <Card className="bg-zinc-900/60 border-white/5">
                  <CardContent className="p-6 space-y-6">
                    {/* Title */}
                    <div className="space-y-2" data-testid="gen-title-section">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest font-medium">
                          <Type className="w-3.5 h-3.5" /> Title
                        </div>
                        <CopyBtn text={gen.title} field={`title-${gen.id}`} testId={`copy-title-${i}`} />
                      </div>
                      <p className="font-heading text-xl font-semibold leading-tight">{gen.title}</p>
                    </div>

                    {/* Description */}
                    <div className="space-y-2" data-testid="gen-description-section">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest font-medium">
                          <FileText className="w-3.5 h-3.5" /> Description
                        </div>
                        <CopyBtn text={gen.description} field={`desc-${gen.id}`} testId={`copy-description-${i}`} />
                      </div>
                      <div className="p-4 rounded-lg bg-secondary/30 border border-white/5">
                        <p className="text-sm leading-relaxed">{gen.description}</p>
                        <p className="text-xs text-muted-foreground/60 mt-2">{gen.description?.length || 0}/280 chars</p>
                      </div>
                    </div>

                    {/* Hashtags */}
                    <div className="space-y-2" data-testid="gen-hashtags-section">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest font-medium">
                          <Hash className="w-3.5 h-3.5" /> Hashtags
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary h-7 px-2 gap-1" data-testid={`copy-hashtags-${i}`}>
                              {copiedField === `hash-${gen.id}` || copiedField === `hash-comma-${gen.id}` || copiedField === `hash-inline-${gen.id}`
                                ? <Check className="w-3.5 h-3.5" />
                                : <><Copy className="w-3.5 h-3.5" /><ChevronDown className="w-3 h-3" /></>
                              }
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="text-xs">
                            <DropdownMenuItem onClick={() => handleCopy(htags.map(t => `#${t}`).join(' '), `hash-${gen.id}`)}>
                              <Hash className="w-3 h-3 mr-2" /> Copy as #hashtags
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleCopy(htags.map(t => `#${t}`).join(', '), `hash-comma-${gen.id}`)}>
                              <Hash className="w-3 h-3 mr-2" /> Copy as #hashtags (comma)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleCopy(htags.join(', '), `hash-inline-${gen.id}`)}>
                              <Tag className="w-3 h-3 mr-2" /> Copy as tags (no #)
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {htags.map((h, j) => (
                          <Badge key={j} variant="secondary" className="bg-indigo-500/10 text-indigo-400 border-0">
                            #{h}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Tags (comma-separated for YouTube etc) */}
                    {tags.length > 0 && (
                      <div className="space-y-2" data-testid="gen-tags-section">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest font-medium">
                            <Tag className="w-3.5 h-3.5" /> Tags
                          </div>
                          <CopyBtn text={tags.join(', ')} field={`tags-${gen.id}`} testId={`copy-tags-${i}`} />
                        </div>
                        <div className="p-3 rounded-lg bg-secondary/20 border border-white/5">
                          <p className="text-sm text-muted-foreground">{tags.join(', ')}</p>
                        </div>
                      </div>
                    )}

                    {/* CTA Suggestion */}
                    {gen.cta_suggestion && (
                      <div className="space-y-2" data-testid="gen-cta-section">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest font-medium">
                            <MessageSquare className="w-3.5 h-3.5" /> CTA Suggestion
                          </div>
                          <CopyBtn text={gen.cta_suggestion} field={`cta-${gen.id}`} testId={`copy-cta-${i}`} />
                        </div>
                        <p className="text-sm text-muted-foreground italic">{gen.cta_suggestion}</p>
                      </div>
                    )}

                    {/* Copy All + Timestamp */}
                    <div className="flex items-center justify-between pt-4 border-t border-white/5">
                      <p className="text-xs text-muted-foreground">
                        Generated {new Date(gen.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <Button onClick={() => handleCopyAll(gen)} variant="secondary" size="sm" className="rounded-full" data-testid={`copy-all-${i}`}>
                        {copiedField === `all-${gen.id}` ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                        Copy All
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>
      )}

      {/* Transcript Block */}
      {video?.transcript && (
        <Card className="bg-zinc-900/60 border-white/5" data-testid="transcript-block">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest font-medium">
                <FileText className="w-3.5 h-3.5" /> Transcript
              </div>
              <Button variant="ghost" size="sm"
                onClick={() => handleCopy(video.transcript, 'transcript')}
                className="text-muted-foreground hover:text-primary h-7 px-2 gap-1"
                data-testid="copy-transcript">
                {copiedField === 'transcript' ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                Copy Transcript
              </Button>
            </div>
            <div className="p-4 rounded-lg bg-secondary/20 border border-white/5 max-h-[400px] overflow-y-auto">
              <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">{video.transcript}</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
